import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Globe, Users, Handshake, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { STAGE_LABELS, label } from '@/lib/labels';
import AgentActivityPanel from '@/components/AgentActivityPanel';
import EmptyState from '@/components/EmptyState';

const ENGAGEMENT_LABELS = {
  speaker: 'Speaker', 'co-host': 'Co-host', panelist: 'Panelista',
  trainer: 'Formatore', advisor: 'Advisor', partner: 'Partner',
  referral: 'Referral', workshop: 'Workshop', webinar: 'Webinar',
};

export default function NetworkPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [segments, setSegments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        api.get('/crm/contacts', { params: { limit: 100 } }).catch(() => ({ data: { contacts: [] } })),
        api.get('/crm/contact-segments').catch(() => ({ data: {} })),
      ]);
      const all = pRes.data.contacts || pRes.data || [];
      setPartners(all.filter(c => c.engagement_ideas?.length > 0 || c.engagements?.length > 0));
      setSegments(sRes.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runReactivation = async () => {
    setSweeping(true);
    try {
      const res = await api.post('/partnership/reactivation-sweep');
      toast.success(`Sweep: ${res.data.reactivations_queued || 0} riattivazioni in coda`);
      fetchAll();
    } catch { toast.error('Errore sweep'); }
    finally { setSweeping(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#2E75B6]" /></div>;

  if (partners.length === 0) {
    return (
      <div className="animate-fade-in" data-testid="network-page">
        <div className="mb-6">
          <h1 className="page-title">Network & Partnership</h1>
          <p className="page-subtitle">Gestisci il network di professionisti AI in Svizzera.</p>
        </div>
        <EmptyState icon={Globe} title="Nessun contatto nel network"
          description="Il network si popola con contatti che hanno 'engagement_ideas' nel CRM (speaker, partner, advisor, co-host). Arricchisci i profili dei contatti dal CRM per vederli qui."
          actionLabel="Vai al CRM" onAction={() => navigate('/contacts')} />
      </div>
    );
  }

  const speakerCount = partners.filter(p => p.engagement_ideas?.includes('speaker')).length;
  const partnerCount = partners.filter(p => p.engagement_ideas?.includes('partner')).length;
  const dormientCount = partners.filter(p => p.relationship_status === 'dormiente').length;

  return (
    <div className="animate-fade-in space-y-6" data-testid="network-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Network & Partnership</h1>
          <p className="page-subtitle">Gestisci il network di professionisti AI in Svizzera. Speaker, partner, advisor, co-host — relazioni strategiche per la crescita di HumanAImpact.</p>
        </div>
        <Button onClick={runReactivation} disabled={sweeping} className="bg-[#1A3A5C] hover:bg-[#2E75B6] text-xs h-8" data-testid="sweep-btn">
          {sweeping ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Sweep Riattivazione
        </Button>
      </div>

      <AgentActivityPanel
        agents={['partnership_agent', 'crm_agent']}
        areaLabel="Network & Partnership"
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E2E8F0]"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-[#1A3A5C]" data-testid="network-total">{partners.length}</p>
          <p className="text-xs text-[#64748B]">Contatti nel network</p>
        </CardContent></Card>
        <Card className="border-[#E2E8F0]"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-[#1A3A5C]" data-testid="network-speakers">{speakerCount}</p>
          <p className="text-xs text-[#64748B]">Speaker potenziali</p>
        </CardContent></Card>
        <Card className="border-[#E2E8F0]"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-[#1A3A5C]" data-testid="network-partners">{partnerCount}</p>
          <p className="text-xs text-[#64748B]">Partner</p>
        </CardContent></Card>
        <Card className="border-[#E2E8F0]"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600" data-testid="network-dormant">{dormientCount}</p>
          <p className="text-xs text-[#64748B]">Dormienti</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="bg-[#F8FAFC] border border-[#E2E8F0] mb-4">
          <TabsTrigger value="all" data-testid="tab-all">Tutti ({partners.length})</TabsTrigger>
          <TabsTrigger value="speaker" data-testid="tab-speakers">Speaker</TabsTrigger>
          <TabsTrigger value="partner" data-testid="tab-partners">Partner</TabsTrigger>
          <TabsTrigger value="competenze" data-testid="tab-competenze">Per competenza</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card className="border-[#E2E8F0]">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F8FAFC]">
                  <TableHead className="text-xs">Nome</TableHead>
                  <TableHead className="text-xs">Azienda</TableHead>
                  <TableHead className="text-xs">Coinvolgimento</TableHead>
                  <TableHead className="text-xs">Stage CRM</TableHead>
                  <TableHead className="text-xs">Relazione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-[#F8FAFC]" onClick={() => navigate(`/contacts?open=${p.id}`)} data-testid={`partner-${p.id}`}>
                    <TableCell>
                      <span className="text-sm font-medium text-[#1A3A5C]">{p.full_name}</span>
                      {p.email && <span className="block text-xs text-[#64748B]">{p.email}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-[#64748B]">{p.company || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.engagement_ideas || []).map(e => <Badge key={e} variant="outline" className="text-[10px]">{ENGAGEMENT_LABELS[e] || e}</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell><Badge className="text-xs bg-slate-100 text-slate-700">{p.stage} — {label(STAGE_LABELS, p.stage)}</Badge></TableCell>
                    <TableCell className="text-sm text-[#64748B]">{p.relationship_status || '-'}</TableCell>
                  </TableRow>
                ))}
                {partners.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#94A3B8]">Nessun contatto nel network. Aggiungi "engagement_ideas" ai contatti CRM per vederli qui.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="speaker">
          <Card className="border-[#E2E8F0]">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F8FAFC]">
                  <TableHead className="text-xs">Nome</TableHead>
                  <TableHead className="text-xs">Competenze</TableHead>
                  <TableHead className="text-xs">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.filter(p => p.engagement_ideas?.includes('speaker')).map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-[#F8FAFC]" onClick={() => navigate(`/contacts?open=${p.id}`)}>
                    <TableCell>
                      <span className="text-sm font-medium text-[#1A3A5C]">{p.full_name}</span>
                      {p.company && <span className="block text-xs text-[#64748B]">{p.company}</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.expertise_tags || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#64748B]">{p.relationship_status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="partner">
          <Card className="border-[#E2E8F0]">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F8FAFC]">
                  <TableHead className="text-xs">Nome</TableHead>
                  <TableHead className="text-xs">Tipo</TableHead>
                  <TableHead className="text-xs">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.filter(p => p.engagement_ideas?.includes('partner') || p.engagement_ideas?.includes('advisor')).map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-[#F8FAFC]" onClick={() => navigate(`/contacts?open=${p.id}`)}>
                    <TableCell>
                      <span className="text-sm font-medium text-[#1A3A5C]">{p.full_name}</span>
                      {p.company && <span className="block text-xs text-[#64748B]">{p.company}</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.engagement_ideas || []).filter(e => ['partner', 'advisor', 'referral'].includes(e)).map(e => <Badge key={e} variant="outline" className="text-[10px]">{ENGAGEMENT_LABELS[e] || e}</Badge>)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#64748B]">{p.relationship_status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="competenze">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(segments?.by_engagement_idea || []).map(seg => (
              <Card key={seg._id} className="border-[#E2E8F0] cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/contacts?engagement_idea=${seg._id}`)} data-testid={`segment-${seg._id}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#1A3A5C]">{seg.count}</p>
                  <p className="text-xs text-[#64748B]">{ENGAGEMENT_LABELS[seg._id] || seg._id}</p>
                </CardContent>
              </Card>
            ))}
            {(!segments?.by_engagement_idea || segments.by_engagement_idea.length === 0) && (
              <div className="col-span-full text-center py-8 text-[#94A3B8] text-sm">Nessun segmento disponibile</div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
